import { inject, Injectable, Injector, runInInjectionContext } from '@angular/core';
import { HttpClient } from "@angular/common/http";
import { ActionCodeSettings, Auth, authState, signInWithEmailAndPassword, createUserWithEmailAndPassword, signOut, User, getIdToken, GoogleAuthProvider, OAuthProvider, signInWithPopup, setPersistence, browserLocalPersistence, browserSessionPersistence, sendPasswordResetEmail, RecaptchaVerifier, signInWithPhoneNumber, signInWithCustomToken, ConfirmationResult, EmailAuthProvider, reauthenticateWithCredential, reauthenticateWithPopup } from '@angular/fire/auth';
import { from, Observable, of, throwError } from 'rxjs';
import { catchError, map, switchMap } from 'rxjs/operators';
import { environment } from 'src/environments/environment';
import { SessionStorageService } from '../services/session-storage.service';

@Injectable({
    providedIn: 'root'
})
export class AuthService {
    private auth: Auth = inject(Auth);
    private injector = inject(Injector);
    recaptchaVerifier: RecaptchaVerifier | null = null;
    confirmationResult: ConfirmationResult | null = null;
    private whatsAppOtpChallengeId: string | null = null;
    userData: Observable<User | null> = authState(this.auth);

    constructor(private http: HttpClient, private sessionStorageService: SessionStorageService) { }

    get usesWhatsAppOtp(): boolean {
        return environment.whatsAppOtpEnabled;
    }

    login(emailAddress: string, password: string, rememberMe: boolean = false): Observable<User | null> {
        const normalizedEmailAddress = emailAddress.trim().toLowerCase();
        const persistence = rememberMe ? browserLocalPersistence : browserSessionPersistence;
        return from(setPersistence(this.auth, persistence)).pipe(
            switchMap(() => from(signInWithEmailAndPassword(this.auth, normalizedEmailAddress, password))),
            map(userCredential => userCredential.user),
            catchError(err => {
                console.log('Something went wrong:', err.message);
                return throwError(() => err);
            })
        );
    }

    register(email: string, password: string): Observable<User | null> {
        const normalizedEmailAddress = email.trim().toLowerCase();
        return from(createUserWithEmailAndPassword(this.auth, normalizedEmailAddress, password)).pipe(
            map(userCredential => {
                console.log('You are Successfully signed up!', userCredential);
                return userCredential.user;
            }),
            catchError(error => {
                console.log('Something is wrong:', error.message);
                return throwError(() => error);
            })
        );
    }

    getCurrentUserAccessToken(): Observable<string> {
        const authStateReady = this.auth.authStateReady ? this.auth.authStateReady() : Promise.resolve();
        return from(authStateReady).pipe(
            switchMap(() => {
                const user = this.auth.currentUser;
                if (!user) {
                    return throwError(() => new Error('No user is currently signed in.'));
                }
                return from(runInInjectionContext(this.injector, () => getIdToken(user, true)));
            })
        );
    }

    hasCurrentUser(): boolean {
        return !!this.auth.currentUser;
    }

    reauthenticateWithPassword(password: string): Observable<string> {
        const user = this.auth.currentUser;
        if (!user?.email || !password) {
            return throwError(() => new Error('Enter your current password to continue.'));
        }
        const credential = EmailAuthProvider.credential(user.email, password);
        return from(reauthenticateWithCredential(user, credential)).pipe(
            switchMap(() => from(getIdToken(user, true)))
        );
    }

    reauthenticateWithGoogle(): Observable<string> {
        const user = this.auth.currentUser;
        if (!user) {
            return throwError(() => new Error('Sign in before reauthenticating.'));
        }
        return from(reauthenticateWithPopup(user, new GoogleAuthProvider())).pipe(
            switchMap(() => from(getIdToken(user, true)))
        );
    }

    reauthenticateWithMicrosoft(): Observable<string> {
        const user = this.auth.currentUser;
        if (!user) {
            return throwError(() => new Error('Sign in before reauthenticating.'));
        }
        const provider = new OAuthProvider('microsoft.com');
        provider.setCustomParameters({ prompt: 'select_account' });
        return from(reauthenticateWithPopup(user, provider)).pipe(
            switchMap(() => from(getIdToken(user, true)))
        );
    }

    activateCurrentBrowserSession(): Observable<void> {
        this.sessionStorageService.ensureBrowserSessionId();
        return this.http.post<void>(`${environment.restApiUrl}sessions/activate`, {});
    }

    checkCurrentBrowserSession(): Observable<{ status: string }> {
        return this.http.get<{ status: string }>(`${environment.restApiUrl}sessions/current`);
    }

    // The refresh token is not directly exposed in the new modular SDK for security reasons.
    // It's handled automatically by the SDK. This method should be re-evaluated if needed.
    /*
    getCurrentUserRefreshToken(): Observable<string> {
        return new Observable((observer) => {
            this.auth.currentUser.then(res => {
                console.log('Refresh Token: ', res?.getIdToken());
                let userRefreshToken = res?.refreshToken;
                if (userRefreshToken !== null) {
                    console.log('Refresh Token: ', userRefreshToken);
                    observer.next(userRefreshToken!);
                } else {
                    console.log('Something is wrong:');
                    observer.error(new Error("refresh token is null"));
                }
            })
                .catch(error => {
                    console.log('Something is wrong:', error.message);
                    observer.error(error);
                });
        });
    }
    */

    logout(): Observable<void> {
        return from(signOut(this.auth));
    }

    loginWithGoogle(rememberMe: boolean = false): Observable<User | null> {
        const provider = new GoogleAuthProvider();
        const persistence = rememberMe ? browserLocalPersistence : browserSessionPersistence;
        return from(setPersistence(this.auth, persistence)).pipe(
            switchMap(() => from(signInWithPopup(this.auth, provider))),
            map(userCredential => userCredential.user),
            catchError(err => {
                console.log('Something went wrong:', err.message);
                return throwError(() => err);
            })
        );
    }

    loginWithMicrosoft(rememberMe: boolean = false): Observable<User | null> {
        const provider = new OAuthProvider('microsoft.com');
        provider.setCustomParameters({ prompt: 'select_account' });
        const persistence = rememberMe ? browserLocalPersistence : browserSessionPersistence;
        return from(setPersistence(this.auth, persistence)).pipe(
            switchMap(() => from(signInWithPopup(this.auth, provider))),
            map(userCredential => userCredential.user),
            catchError(err => {
                console.log('Something went wrong:', err.message);
                return throwError(() => err);
            })
        );
    }

    registerOrSignIn(email: string, password: string): Observable<User | null> {
        const normalizedEmailAddress = email.trim().toLowerCase();
        return from(createUserWithEmailAndPassword(this.auth, normalizedEmailAddress, password)).pipe(
            map(userCredential => userCredential.user),
            catchError(error => {
                if (error?.code !== 'auth/email-already-in-use') {
                    return throwError(() => error);
                }
                return from(signInWithEmailAndPassword(this.auth, normalizedEmailAddress, password)).pipe(
                    map(userCredential => userCredential.user)
                );
            })
        );
    }

    requestPasswordReset(email: string): Observable<void> {
        const normalizedEmailAddress = email.trim().toLowerCase();
        if (!normalizedEmailAddress) {
            return throwError(() => new Error('Please enter your email address.'));
        }

        return from(sendPasswordResetEmail(this.auth, normalizedEmailAddress, this.passwordResetActionCodeSettings())).pipe(
            map(() => undefined),
            catchError(error => {
                if (error?.code === 'auth/user-not-found') {
                    return of(undefined);
                }
                return throwError(() => new Error(this.passwordResetErrorMessage(error)));
            })
        );
    }

    sendPasswordResetEmail(email: string): Observable<void> {
        return this.requestPasswordReset(email);
    }

    initializeRecaptcha(containerId: string): void {
        if (this.usesWhatsAppOtp) {
            return;
        }
        if (this.recaptchaVerifier) {
            this.recaptchaVerifier.clear();
        }
        this.recaptchaVerifier = new RecaptchaVerifier(this.auth, containerId, {
            'size': 'invisible',
            'callback': (response: any) => {
                // reCAPTCHA solved, allow signInWithPhoneNumber.
            }
        });
    }

    sendOtp(phoneNumber: string): Observable<ConfirmationResult | null> {
        if (this.usesWhatsAppOtp) {
            return this.http.post<{ challengeId: string }>(`${environment.restApiUrl}auth/whatsapp-otp/request`, {
                phoneNumber,
                client: 'ENTERPRISE_PORTAL',
                locale: navigator.language
            }).pipe(
                map(response => {
                    this.whatsAppOtpChallengeId = response.challengeId;
                    return null;
                })
            );
        }
        if (!this.recaptchaVerifier) {
            return throwError(() => new Error('reCAPTCHA verifier not initialized.'));
        }
        return from(signInWithPhoneNumber(this.auth, phoneNumber, this.recaptchaVerifier)).pipe(
            map(confirmationResult => {
                this.confirmationResult = confirmationResult;
                return confirmationResult;
            }),
            catchError(err => {
                console.error('Error sending OTP:', err);
                this.recaptchaVerifier?.clear();
                return throwError(() => err);
            })
        );
    }

    verifyOtp(otp: string): Observable<User | null> {
        if (this.usesWhatsAppOtp) {
            if (!this.whatsAppOtpChallengeId) {
                return throwError(() => new Error('No WhatsApp OTP request is active. Request a new code.'));
            }
            return this.http.post<{ customToken: string }>(`${environment.restApiUrl}auth/whatsapp-otp/verify`, {
                challengeId: this.whatsAppOtpChallengeId,
                client: 'ENTERPRISE_PORTAL',
                code: otp
            }).pipe(
                switchMap(response => from(signInWithCustomToken(this.auth, response.customToken))),
                map(userCredential => {
                    this.whatsAppOtpChallengeId = null;
                    return userCredential.user;
                })
            );
        }
        if (!this.confirmationResult) {
            return throwError(() => new Error('No OTP confirmation pending.'));
        }
        return from(this.confirmationResult.confirm(otp)).pipe(
            map(userCredential => userCredential.user),
            catchError(err => {
                console.error('Error verifying OTP:', err);
                return throwError(() => err);
            })
        );
    }

    async updateFirebaseUser(firebaseId: string, emailAddress: string, phone: string) {
        const url = 'https://us-central1-prologix-test.cloudfunctions.net/firebaseUpdateUserAPI';
        const headers = { 'Content-Type': 'application/json' };

        try {
            const response = await this.http.post(url, {
                'uid': firebaseId,
                'email': emailAddress,
                'phoneNumber': phone
            }, { headers }).toPromise();
            console.log('Firebase user updated:', response);
        } catch (error) {
            console.error('Error updating Firebase user:', error);
            throw error;
        }
    }

    async createFirebaseUser(email: string, phoneNumber: string): Promise<string> {
        if (!email) {
            email = phoneNumber.replace(/\+/g, '') + '@abcxyz.com';
        }
        console.log('Generated Email: ' + email);
        let userCredential;
        try {
            userCredential = await signInWithEmailAndPassword(this.auth, email, 'temporaryPassword');
        } catch (error) {
            // Assuming error means user does not exist, so create a new one.
            // A more robust implementation would check the error code.
            userCredential = await createUserWithEmailAndPassword(this.auth, email, 'temporaryPassword');
        }
        await this.updateFirebaseUser(userCredential.user.uid, email, phoneNumber);
        return userCredential.user.uid;
    }

    private passwordResetActionCodeSettings(): ActionCodeSettings | undefined {
        if (typeof window === 'undefined' || !window.location?.origin) {
            return undefined;
        }

        return {
            url: `${window.location.origin}/login`,
            handleCodeInApp: false
        };
    }

    private passwordResetErrorMessage(error: any): string {
        switch (error?.code) {
            case 'auth/invalid-email':
                return 'Enter a valid email address.';
            case 'auth/missing-email':
                return 'Please enter your email address.';
            case 'auth/too-many-requests':
                return 'Too many reset attempts were made. Please wait a few minutes and try again.';
            default:
                return error?.message || 'Unable to send a password reset email right now.';
        }
    }
}
